import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import {
  importErrorsDef,
  importErrorsDefIsMobile,
} from "my-hr/configs/my-hr-table-defs";
import { OcDataTable } from "oc/components";
import { initialTableData } from "oc/configs/initial-table-data";
import { useOcTableState } from "oc/hooks";

interface MyHrUsersImportResultProps {
  importUsersResult: {
    importErrors: Array<Record<string, any>>;
    failedUsersCount: number;
    totalProcessedRecordCount: number;
    importedUsersCount: number;
    [key: string]: any;
  };
  isMobile: boolean;
}

export const MyHrUsersImportResult = ({
  importUsersResult,
  isMobile,
}: MyHrUsersImportResultProps) => {
  const { t } = useTranslation();

  const tableId = "errorsListTable";
  const [tableData, setTableData] = useOcTableState(tableId, {
    ...initialTableData,
    sortField: "employeeName",
    pageSize: 5,
  });
  const [selectedRowId, setSelectedRowId] = useState<string | undefined>(undefined);

  const showImportResult =
    !!importUsersResult && Object.keys(importUsersResult).length !== 0;

  const errors: Array<Record<string, any>> = Array.isArray(
    importUsersResult?.importErrors
  )
    ? importUsersResult.importErrors
    : [];

  const searchText = tableData?.searchText;
  const columnName = tableData?.searchField;

  const filteredResult =
    searchText && columnName
      ? errors.filter((item) => new RegExp(searchText, "i").test(item?.[columnName]))
      : errors;

  if (!showImportResult) return null;

  const importData = {
    rows: tableData?.searchText?.length === 0 ? errors : filteredResult,
    total: errors.length,
    page: tableData?.page,
    pageSize: tableData?.pageSize,
    searchField: tableData?.searchField,
    searchText: tableData?.searchText,
  };

  const tableDef = isMobile ? importErrorsDefIsMobile : importErrorsDef;

  const filteredTableDef = {
    id: importErrorsDef?.id,
    columns: importErrorsDef?.columns?.filter(
      (column) =>
        !importErrorsDefIsMobile?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  const onSelectRow = (o: { _id?: string } | null) => {
    if (isMobile) {
      setSelectedRowId(selectedRowId === o?._id ? undefined : o?._id ?? undefined);
    }
  };

  return (
    <Box>
      {importUsersResult?.failedUsersCount === 0 ? (
        <>
          <Box
            fontSize="48px"
            textAlign="center"
            paddingBottom="32px"
            style={{ color: "#2FA84F" }}
          >
            {t("MY_HR_IMPORT_HAS_DONE")}
          </Box>
          <Box>
            {importUsersResult?.totalProcessedRecordCount}{" "}
            {t("MY_HR_ITEM_PROCESSED")}
          </Box>
          <Box>
            {importUsersResult?.importedUsersCount}{" "}
            {t("MY_HR_EMPLOYEE_IMPORTED")}
          </Box>
        </>
      ) : (
        <Box>
          <Box
            fontSize="48px"
            textAlign={isMobile ? "left" : "center"}
            paddingBottom="32px"
            style={{ color: "#F3AA18" }}
          >
            {t("MY_HR_IMPORT_HAS_DONE_WITH_ERROR")}
          </Box>
          <Box>
            {importUsersResult?.totalProcessedRecordCount}{" "}
            {t("MY_HR_ITEM_PROCESSED")}
          </Box>
          <Box>
            {importUsersResult?.importedUsersCount}{" "}
            {t("MY_HR_EMPLOYEE_IMPORTED")}
          </Box>
          <Box>
            {importUsersResult?.failedUsersCount}{" "}
            {t("MY_HR_ERROR_COUNT_ON_IMPORT")}
          </Box>
          <Box height="32px" />
          <OcDataTable
            data={importData}
            loading={false}
            tableDef={tableDef}
            tableData={tableData}
            setTableData={setTableData}
            pagination={true}
            localPaging={true}
            onSelectRow={onSelectRow}
            filteredTableDef={filteredTableDef}
            selectedRowId={selectedRowId}
          />
          <Box padding="16px" />
        </Box>
      )}
    </Box>
  );
};

export default MyHrUsersImportResult;
